<div class="min-h-dvh bg-white">
  <app-site-header [tinted]="true"></app-site-header>

  <main>
    <!-- HERO -->
    <section class="bg-primary">
      <div class="fade-up max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div class="max-w-2xl">
          <p class="flex flex-wrap items-center gap-2 mb-6">
            <span class="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white">
              <span aria-hidden="true" class="live-dot size-2 rounded-full bg-accent"></span>
              Now recruiting
            </span>
            <span class="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white/80">
              <mat-icon inline>public</mat-icon>
              100% remote
            </span>
          </p>

          <h1 class="text-display-mobile md:text-display text-white">
            Your service year just got a
            <span class="whitespace-nowrap rounded-lg bg-accent px-2 text-primary">side hustle.</span>
          </h1>

          <p class="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
            Become a corpers.ng ambassador. Earn real cash, build your CV, and help fellow corps members navigate NYSC - all while on camp or posted.
            <strong class="font-medium text-white">Work fully remote, on your own terms.</strong>
          </p>

          <div class="mt-8 flex flex-wrap items-center gap-3">
            <a [href]="applyUrl" target="_blank" rel="noopener" class="btn-accent inline-flex min-h-12 items-center gap-2 !py-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
              Become an ambassador <mat-icon inline>arrow_forward</mat-icon>
            </a>
            <a [href]="whatsappUrl" target="_blank" rel="noopener"
              class="inline-flex min-h-12 items-center gap-2 rounded-lg border-2 border-white/40 px-6 font-medium text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
              <ng-container *ngTemplateOutlet="whatsappIcon"></ng-container>
              Get exclusive updates
            </a>
          </div>

          <dl class="mt-12 flex flex-wrap gap-10">
            <div class="flex flex-col-reverse">
              <dt class="mt-1 text-sm text-white/70">Per housing referral</dt>
              <dd class="text-3xl font-bold text-white">&#8358;10K</dd>
            </div>
            <div class="flex flex-col-reverse">
              <dt class="mt-1 text-sm text-white/70">Ambassador benefits</dt>
              <dd class="text-3xl font-bold text-white">{{ benefits.length }}</dd>
            </div>
            <div class="flex flex-col-reverse">
              <dt class="mt-1 text-sm text-white/70">Weekly target</dt>
              <dd class="text-3xl font-bold text-white">{{ weeklyTargetPoints }} pts</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <!-- THE PROBLEM -->
    <section class="section-spacing bg-background-light">
      <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <p class="mb-2 text-sm font-semibold uppercase tracking-wider text-primary/70">The problem we solve</p>
        <h2 class="mb-10 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-primary">NYSC shouldn't feel like starting from zero</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div *ngFor="let pain of painPoints" class="rounded-xl bg-white p-6 shadow-card">
            <mat-icon class="mb-3 text-primary">{{ pain.icon }}</mat-icon>
            <h3 class="mb-2 text-lg font-semibold text-primary">{{ pain.title }}</h3>
            <p class="text-sm leading-relaxed text-text-secondary">{{ pain.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- YOUR ROLE -->
    <section class="section-spacing bg-primary" id="what-you-do">
      <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <h2 class="mb-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">Your role as an ambassador</h2>
        <p class="mb-10 max-w-xl leading-relaxed text-white/80">
          We make the NYSC journey safer, simpler and more meaningful for every corps member. You'll contribute to these solutions.
        </p>
        <ol class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <li *ngFor="let role of roles" class="rounded-xl bg-white/5 p-6 ring-1" [ngClass]="role.badge ? 'ring-accent' : 'ring-white/15'">
            <span class="mb-4 flex size-10 items-center justify-center rounded-lg bg-accent font-bold text-primary">{{ role.num }}</span>
            <h3 class="mb-2 text-lg font-semibold text-white">{{ role.title }}</h3>
            <p class="text-sm leading-relaxed text-white/80">{{ role.description }}</p>
            <span *ngIf="role.badge" class="mt-4 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-sm font-medium text-primary">
              <mat-icon inline>flag</mat-icon>{{ role.badge }}
            </span>
          </li>
        </ol>
      </div>
    </section>

    <!-- BENEFITS -->
    <section class="section-spacing bg-background-light">
      <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <h2 class="mb-10 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-primary">What you get as an ambassador</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <div *ngFor="let benefit of benefits" class="flex gap-4 rounded-xl bg-white p-6 shadow-card">
            <span class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg bg-background-light text-primary">
              <mat-icon>{{ benefit.icon }}</mat-icon>
            </span>
            <div>
              <h3 class="mb-1 text-lg font-semibold text-primary">{{ benefit.title }}</h3>
              <p class="text-sm leading-relaxed text-text-secondary">{{ benefit.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- POINTS -->
    <section class="section-spacing bg-primary">
      <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <p class="mb-2 text-sm font-semibold uppercase tracking-wider text-white/70">Point system</p>
        <h2 class="mb-10 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">Every action earns points</h2>
        <ul class="mx-auto grid max-w-4xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <li *ngFor="let point of points" class="flex items-center gap-4 rounded-xl bg-white/5 p-4 text-left ring-1 ring-white/15">
            <span class="flex-shrink-0 rounded-lg bg-accent px-3 py-1 text-xl font-bold text-primary">{{ point.pts }}</span>
            <span class="text-sm font-medium leading-snug text-white">{{ point.label }}</span>
          </li>
        </ul>
        <p class="mt-8 text-white/80">
          Weekly target: <strong class="font-semibold text-white">{{ weeklyTargetPoints }} pts</strong> - hit it, earn your bonus.
        </p>
      </div>
    </section>

    <!-- APPLY -->
    <section id="contact" class="scroll-mt-24 section-spacing bg-accent">
      <div class="mx-auto max-w-2xl px-6 text-center">
        <h2 class="mb-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-primary">Ready to turn your service year into a launchpad?</h2>
        <p class="mb-8 text-lg leading-relaxed text-primary/80">
          Apply with the form below or jump straight into the WhatsApp community. It takes under a minute.
        </p>
        <div class="mb-6 flex flex-wrap justify-center gap-3">
          <a [href]="applyUrl" target="_blank" rel="noopener" class="btn-primary inline-flex min-h-12 items-center gap-2 !py-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
            Apply now - fill the form <mat-icon inline>arrow_forward</mat-icon>
          </a>
          <a [href]="whatsappUrl" target="_blank" rel="noopener" class="btn-secondary inline-flex min-h-12 items-center gap-2 !py-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
            <ng-container *ngTemplateOutlet="whatsappIcon"></ng-container>
            Get exclusive updates
          </a>
        </div>
        <p class="text-primary/80">
          Or email <a href="mailto:contact@corpers.ng" class="font-medium text-primary underline hover:text-primary-light">contact&#64;corpers.ng</a>
        </p>
      </div>
    </section>
  </main>

  <!-- FOOTER -->
  <footer class="bg-primary-dark">
    <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8 flex flex-wrap items-center justify-between gap-4">
      <a routerLink="/" class="flex-shrink-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
        <img ngSrc="assets/images/logo_transparent_rectangle.png" alt="corpers.ng" class="w-24" width="410" height="65">
      </a>
      <div class="flex flex-wrap items-center gap-3">
        <a [href]="applyUrl" target="_blank" rel="noopener"
          class="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-white/30 px-4 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/10">
          Apply now <mat-icon inline>arrow_forward</mat-icon>
        </a>
        <a [href]="whatsappUrl" target="_blank" rel="noopener"
          class="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-white/30 px-4 text-sm font-medium text-white transition-colors duration-200 hover:bg-white/10">
          <ng-container *ngTemplateOutlet="whatsappIcon"></ng-container>
          WhatsApp
        </a>
      </div>
      <p class="w-full text-center text-sm text-white/60 md:w-auto md:text-right">
        &copy; {{ year }} corpers.ng &middot; The corps member's trusted platform
      </p>
    </div>
  </footer>
</div>

<!-- The WhatsApp mark, in the text colour around it -->
<ng-template #whatsappIcon>
  <svg aria-hidden="true" class="size-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
</ng-template>
